import { logger } from '@adornis/base/logging.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../ui/sg-snackbar.js';
import { JobTicketConsentFields } from './JobTicketConsentFields.js';
import { JobTicketPersonalFields } from './JobTicketPersonalFields.js';
import { JobTicketValidityFields } from './JobTicketValidityFields.js';
import {
  confirmTicketOrder,
  saveJobTicketData,
  sendTicketInvite,
  showJobTicketValidationErrors,
} from './jobTicketActions.js';
import { buildJobTicketFormData, getPersistedJobTicketDates, type JobTicketPerson } from './jobTicketPerson.js';
import { buildJobTicketValidator, translateJobTicketError } from '../../../db/utils/jobTicketValidator.js';

type PendingAction = 'save' | 'invite' | 'order' | null;

export interface JobTicketEditorProps {
  person: JobTicketPerson;
  /** may act on behalf of this person (send invites, save) */
  hasEditPermission: boolean;
  /** may edit the form fields - always true for one's own ticket */
  canEditFields: boolean;
  /** global ticket administrators may save without running the form validation */
  canSaveWithoutValidation: boolean;
}

/**
 * The job ticket form of a single person: personal data, validity period and the consents needed to
 * order. Owns the form state, so it deliberately does not reset when the person document updates.
 */
export const JobTicketEditor = stateful((props: JobTicketEditorProps) => {
  /**
   * Created once per person: the form must survive updates of the person document (e.g. the
   * subscription emitting right after a save), otherwise unsaved input would be dropped.
   */
  const document = useMemo(() => [buildJobTicketFormData(props.person)], [props.person._id]);
  /** the dates as they were loaded - the legacy validators compared against the loaded doc as well */
  const persistedDates = useMemo(() => [getPersistedJobTicketDates(props.person)], [props.person._id]);

  const form = useForm(
    document,
    {
      validator: buildJobTicketValidator(persistedDates),
      // no edit permission disables every field, mirroring the legacy enabledFields selection set
      enabledFields: props.canEditFields ? null : {},
      translateError: translateJobTicketError,
    },
    [document, props.canEditFields],
  );

  const [pendingAction, setPendingAction] = useState<PendingAction>(null, []);

  const runAction = async (action: Exclude<PendingAction, null>, task: () => Promise<void>) => {
    if (pendingAction) return;
    setPendingAction(action);
    try {
      await task();
    } finally {
      setPendingAction(null);
    }
  };

  const ticketOrdered = Boolean(props.person.mitarbeiterDaten?.deutschlandTicketBestellt);

  const handleSave = async () => {
    if (!props.hasEditPermission) {
      await SgSnackbar.show('Sie haben hierfür nicht die notwendige Berechtigung.');
      return;
    }

    try {
      if (!props.canSaveWithoutValidation) {
        const error = await form.validate();
        if (error) {
          await showJobTicketValidationErrors(error);
          return;
        }
      }

      await saveJobTicketData(props.person._id, form.document);
      await SgSnackbar.show('Erfolgreich gespeichert');
    } catch (e) {
      logger.error(e);
      await alertDialog((e as Error).message);
    }
  };

  const handleOrder = async () => {
    if (!(await confirmTicketOrder(form.document, persistedDates))) return;

    const error = await form.validate();
    if (error) {
      await showJobTicketValidationErrors(error);
      return;
    }

    try {
      await saveJobTicketData(props.person._id, form.document);
      void SgSnackbar.show('Job-Ticket wurde erfolgreich bestellt');
    } catch (e) {
      logger.error(e);
      void SgSnackbar.showError('Es gab einen Fehler beim bestellen des Tickets');
    }
  };

  const spinner = (action: Exclude<PendingAction, null>) =>
    pendingAction === action ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing;

  const inviteButton = () => {
    if (!props.hasEditPermission || ticketOrdered) return nothing;

    return html`
      <button
        class="btn btn-outline"
        ?disabled=${pendingAction !== null}
        @click=${() => runAction('invite', () => sendTicketInvite(props.person))}
      >
        ${spinner('invite')}
        <span class="icon-[material-symbols--mail-outline] text-lg"></span>
        Einladungslink senden
      </button>
    `;
  };

  const saveButton = () => {
    if (!(props.hasEditPermission && ticketOrdered) && !props.canSaveWithoutValidation) return nothing;

    return html`
      <button class="btn btn-primary" ?disabled=${pendingAction !== null} @click=${() => runAction('save', handleSave)}>
        ${spinner('save')}
        <span class="icon-[material-symbols--save] text-lg"></span>
        Speichern
      </button>
    `;
  };

  const orderButton = () => {
    if (ticketOrdered) return nothing;

    const consentsMissing =
      !form.document.jobTicketKenntnis || !form.document.jobTicketDatenweitergabe || !form.document.jobTicketBestaetigt;

    return html`
      <button
        class="btn btn-primary btn-lg"
        ?disabled=${consentsMissing || pendingAction !== null}
        @click=${() => runAction('order', handleOrder)}
      >
        ${spinner('order')}
        <span class="icon-[material-symbols--confirmation-number-outline] text-lg"></span>
        Ticket anfordern
      </button>
    `;
  };

  return html`
    <div class="card-container">
      <div class="card card-body">
        ${CardHeader({
          title: 'Job-Ticket',
          subtitle:
            ticketOrdered && props.person.mitarbeiterDaten?.jobTicketBestellDatum
              ? html`<span class="badge badge-success">Ticket bestellt</span>`
              : undefined,
          actions: html`<div class="flex flex-row gap-2">${inviteButton()}${saveButton()}</div>`,
          actionsDivider: true,
        })}
        ${JobTicketPersonalFields({ form })}
      </div>

      <div class="card card-body">${CardHeader({ title: 'Gültigkeit' })} ${JobTicketValidityFields({ form })}</div>

      <div class="card card-body">
        ${CardHeader({ title: 'Einverständniserklärungen' })} ${JobTicketConsentFields({ form })} ${orderButton()}
      </div>
    </div>
  `;
});
